/* Public /distributions — full distribution history with Upcoming/
 * Completed tabs. Data source: /api/public/distributions.
 * All records are Admin → Updates entries with type=distribution.
 * Only public (is_published + not archived) records are returned by
 * the backend, and only completed records contribute to impact totals. */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, Loader2, MapPin, Package, Truck, X, Calendar, Maximize2
} from 'lucide-react';
import PhotoLightbox from '../components/PhotoLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DistributionsPage = () => {
  const [tab, setTab] = useState('completed');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  // Lightbox state: null when closed, otherwise { startIndex }.
  // Photos array is derived from `selected` so it always mirrors the
  // current distribution's featured + additional order.
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Combined photo list for the currently-selected distribution.
  // Featured first, then additional photos, in their existing order.
  // Deduped so a photo that's both featured AND additional (edge case
  // from admin toggling) doesn't appear twice.
  const galleryPhotos = useMemo(() => {
    if (!selected) return [];
    const combined = [];
    if (selected.image_url) combined.push(selected.image_url);
    if (Array.isArray(selected.additional_photos)) {
      for (const u of selected.additional_photos) {
        if (u && !combined.includes(u)) combined.push(u);
      }
    }
    return combined;
  }, [selected]);

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);

  const fetchItems = useCallback(async (status) => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/public/distributions?status=${status}&limit=100`);
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch { setItems([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(tab); }, [tab, fetchItems]);

  // ESC + scroll-lock on detail modal. When the lightbox is on top,
  // it owns ESC and body scroll — skip our handlers so we don't
  // close the detail modal underneath a lightbox dismiss.
  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => {
      if (lightboxIndex !== null) return;      // lightbox handles it
      if (e.key === 'Escape') setSelected(null);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [selected, lightboxIndex]);

  // Reset the lightbox when the caller navigates away from a
  // distribution so a stray index doesn't reopen it on next select.
  useEffect(() => { if (!selected) setLightboxIndex(null); }, [selected]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Hero */}
      <section className="bg-[#0A0A0A] py-12" data-testid="distributions-hero">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA1826] font-semibold mb-2">Distribution History</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-3">
            Where Your Support Goes
          </h1>
          <p className="text-gray-400 text-sm sm:text-base max-w-xl mx-auto">
            Every completed distribution is documented here — what was distributed, where, and with which community partners.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-6 pt-8">
        <div className="inline-flex bg-white border border-[#E5E7EB] rounded-md p-1" data-testid="distributions-tabs">
          <button type="button"
            onClick={() => setTab('completed')}
            className={`px-4 py-1.5 text-xs font-semibold rounded transition-colors ${tab === 'completed' ? 'bg-[#BA1826] text-white' : 'text-[#4B5563] hover:text-[#0A0A0A]'}`}
            data-testid="tab-completed">Completed</button>
          <button type="button"
            onClick={() => setTab('upcoming')}
            className={`px-4 py-1.5 text-xs font-semibold rounded transition-colors ${tab === 'upcoming' ? 'bg-[#BA1826] text-white' : 'text-[#4B5563] hover:text-[#0A0A0A]'}`}
            data-testid="tab-upcoming">Upcoming</button>
        </div>
      </div>

      {/* List */}
      <section className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[#E5E7EB] rounded-xl" data-testid="distributions-empty">
            <Truck className="w-8 h-8 text-[#9CA3AF] mx-auto mb-3" />
            <p className="text-sm text-[#4B5563] mb-4">
              {tab === 'completed'
                ? 'No completed distributions yet.'
                : 'No upcoming distributions scheduled right now.'}
            </p>
            <Link to="/impact" className="text-xs font-semibold text-[#BA1826] hover:underline">
              Back to Impact →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="distributions-grid">
            {items.map((d) => (
              <button type="button" key={d.id}
                onClick={() => setSelected(d)}
                data-testid={`distribution-item-${d.id}`}
                className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden text-left hover:border-[#BA1826]/40 hover:shadow-sm transition-all group focus:outline-none focus:ring-2 focus:ring-[#BA1826]"
              >
                {d.image_url ? (
                  <div className="h-40 bg-[#F1F5F9] overflow-hidden">
                    <img src={d.image_url} alt={d.title} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="h-14 bg-[#F1F5F9] flex items-center justify-center">
                    <Truck className="w-5 h-5 text-[#9CA3AF]" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${d.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {d.status === 'completed' ? 'Completed' : 'Upcoming'}
                    </span>
                    {d.quantity > 0 && d.item_type && (
                      <span className="text-[11px] font-semibold text-[#0A0A0A]">
                        {d.quantity} {d.item_type}
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading text-sm font-bold text-[#0A0A0A] mb-1 line-clamp-2">{d.title}</h3>
                  <div className="text-[11px] text-[#4B5563] flex flex-wrap gap-x-2">
                    {d.location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3 text-[#BA1826]" />{d.location}</span>}
                    {d.date && <span>· {d.date}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="text-center mt-8">
          <Link to="/impact"
            className="text-xs text-[#4B5563] hover:text-[#BA1826] inline-flex items-center gap-1"
            data-testid="back-to-impact-link">
            <ArrowRight className="w-3 h-3 rotate-180" /> Back to Impact
          </Link>
        </div>
      </section>

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-[95] bg-black/90 overflow-auto"
          onClick={() => setSelected(null)}
          role="dialog" aria-modal="true"
          data-testid="distribution-detail-modal"
        >
          <button type="button"
            onClick={(e) => { e.stopPropagation(); setSelected(null); }}
            className="fixed top-4 right-4 z-10 bg-black/60 text-white p-2 rounded-full hover:bg-white/20"
            aria-label="Close"
            data-testid="distribution-detail-close">
            <X className="w-6 h-6" />
          </button>
          <div className="min-h-full flex items-start justify-center p-4 sm:p-10">
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl max-w-3xl w-full overflow-hidden">
              {selected.image_url && (
                <button type="button"
                  onClick={() => openLightbox(0)}
                  className="block w-full bg-black/70 focus:outline-none focus:ring-2 focus:ring-[#BA1826] group relative"
                  aria-label={`View ${selected.title} photos full-screen`}
                  data-testid="distribution-featured-open">
                  <img src={selected.image_url} alt={selected.title}
                    className="w-full max-h-[420px] object-contain" />
                  <span className="absolute bottom-2 right-2 bg-black/60 text-white rounded-full p-1.5 opacity-90 group-hover:bg-white/20 transition-colors">
                    <Maximize2 className="w-3.5 h-3.5" />
                  </span>
                </button>
              )}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${selected.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {selected.status === 'completed' ? 'Completed' : 'Upcoming'}
                  </span>
                  {selected.quantity > 0 && selected.item_type && (
                    <span className="text-xs font-semibold text-[#0A0A0A] flex items-center gap-1">
                      <Package className="w-3.5 h-3.5 text-[#BA1826]" />
                      {selected.quantity} {selected.item_type}
                    </span>
                  )}
                </div>
                <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0A0A0A] mb-2">
                  {selected.title}
                </h2>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#4B5563] mb-4">
                  {selected.date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{selected.date}</span>}
                  {selected.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-[#BA1826]" />{selected.location}</span>}
                  {selected.partner && <span>Partner: <strong>{selected.partner}</strong></span>}
                  {selected.people_served > 0 && <span>{selected.people_served} people served</span>}
                </div>
                {selected.description && (
                  <p className="text-sm text-[#0A0A0A] leading-relaxed mb-4 whitespace-pre-line">
                    {selected.description}
                  </p>
                )}
                {Array.isArray(selected.additional_photos) && selected.additional_photos.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4"
                       data-testid="distribution-additional-photos">
                    {selected.additional_photos.map((u, i) => {
                      // Lightbox index accounts for the featured photo
                      // sitting at position 0 in `galleryPhotos`.
                      const gIdx = galleryPhotos.indexOf(u);
                      return (
                        <button key={i} type="button"
                          onClick={() => openLightbox(gIdx >= 0 ? gIdx : (selected.image_url ? i + 1 : i))}
                          className="aspect-square bg-[#F1F5F9] rounded overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#BA1826] group relative"
                          aria-label={`View photo ${i + 1} of ${selected.title} full-screen`}
                          data-testid={`distribution-additional-open-${i}`}>
                          <img src={u} alt={`${selected.title} — photo ${i + 1}`} loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                          <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <Maximize2 className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 drop-shadow" />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          <PhotoLightbox
            open={lightboxIndex !== null}
            photos={galleryPhotos}
            startIndex={lightboxIndex || 0}
            title={selected.title}
            onClose={closeLightbox}
          />
        </div>
      )}
    </div>
  );
};

export default DistributionsPage;
